import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const formatList = (items, getLabel) => {
  if (!items || items.length === 0) {
    return <span className="text-gray-400">None</span>;
  }

  return (
    <div className="space-y-1">
      {items.map((item) => (
        <div key={item.id || item.material_id} className="text-sm text-gray-800">
          {getLabel(item)}
        </div>
      ))}
    </div>
  );
};

const CustomPricingList = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCustomPricing = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE}/api/admin/customers/custom-pricing`, {
          credentials: 'include',
        });

        if (!response.ok) {
          const errorPayload = await response.json().catch(() => ({}));
          throw new Error(errorPayload.error || 'Failed to load custom pricing');
        }

        const data = await response.json();
        setCustomers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error loading custom pricing:', err);
        setError(err.message);
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    };

    loadCustomPricing();
  }, []);

  if (loading) {
    return <div className="text-center p-4">Loading custom pricing...</div>;
  }

  if (error) {
    return <div className="text-center p-4 text-red-600">Error loading custom pricing: {error}</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="px-4 py-3 text-sm text-gray-600 border-b border-gray-200">
        Showing {customers.length} customer{customers.length === 1 ? '' : 's'} with custom pricing.
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pricing Tier
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Custom Materials
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                LF Job Types
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                SF Job Types
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Notes
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {customers.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-sm text-gray-500">
                  No customers have custom pricing configured.
                </td>
              </tr>
            ) : (
              customers.map((customer) => (
                <tr key={customer.customer_id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 align-top">
                    <div className="text-sm font-medium text-gray-900">{customer.customer_name}</div>
                    <div className="text-xs text-gray-500">ID: {customer.customer_id}</div>
                    {customer.customer_accounts_code && (
                      <div className="text-xs text-gray-500">Account: {customer.customer_accounts_code}</div>
                    )}
                  </td>
                  <td className="px-4 py-4 align-top text-sm text-gray-700 whitespace-nowrap">
                    {customer.pricing_tier || <span className="text-gray-400">Not set</span>}
                  </td>
                  <td className="px-4 py-4 align-top min-w-[220px]">
                    {formatList(customer.custom_materials, (material) => (
                      <>
                        {material.material_name}{' '}
                        <span className="text-xs text-gray-500">({material.custom_label})</span>
                        {material.included_finishes?.length > 0 && (
                          <div className="text-xs text-gray-500">
                            Includes: {material.included_finishes.map(
                              (id) => id === 'hem-and-eyelet' ? 'Hem & Eyelet' : id
                            ).join(', ')}
                          </div>
                        )}
                      </>
                    ))}
                  </td>
                  <td className="px-4 py-4 align-top min-w-[160px]">
                    {formatList(customer.lf_custom_job_types, (jobType) => jobType.name)}
                  </td>
                  <td className="px-4 py-4 align-top min-w-[160px]">
                    {formatList(customer.sf_custom_job_types, (jobType) => jobType.name)}
                  </td>
                  <td className="px-4 py-4 align-top text-sm text-gray-700 min-w-[220px]">
                    {customer.notes || <span className="text-gray-400">None</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomPricingList;
